import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { Icon } from '../components/Icon.tsx';
import { Button, Dialog, Field, IconButton } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { action, ApiError } from '../lib/api.ts';
import { clock, money } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useNow, useUi } from '../lib/ui-state.tsx';

/**
 * The customer display: a second screen at the register, turned towards the customer. Open it
 * from the till (it opens in its own window, to drag onto the second monitor and make full
 * screen), or sign in on a tablet on the counter and open /display/<register>.
 */

interface Base {
  register: string;
  business: string;
  message: string | null;
}
interface Line {
  id: string;
  name: string;
  combo: string | null;
  qty: number;
  unit_price: number;
  discount: number;
  total: number;
  latest: boolean;
}
type View =
  | (Base & { phase: 'idle' })
  | (Base & {
      phase: 'order' | 'paying';
      number: number;
      lines: Line[];
      count: number;
      subtotal: number;
      discount: number;
      tax: number;
      tax_added: boolean;
      total: number;
      paid: number;
      due: number;
    })
  | (Base & { phase: 'thanks'; total: number; received: number; change: number; methods: string[] });

/** How often the display asks what to show. */
const EVERY_MS = 1500;

export function CustomerDisplay() {
  const { register = '' } = useParams();
  const { can } = useSession();
  const now = useNow();
  const [view, setView] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const list = useRef<HTMLUListElement>(null);

  useEffect(() => {
    document.documentElement.classList.add('cd-root');
    return () => document.documentElement.classList.remove('cd-root');
  }, []);
  useEffect(() => {
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      try {
        const v = await action<View>('pos.display', { register });
        if (stop) return;
        setView(v);
        setError(null);
      } catch (err) {
        if (stop) return;
        // A lost connection keeps the last picture; a register that is gone says so.
        if (err instanceof ApiError && err.status === 404) setError(err.message);
      }
      if (!stop) timer = setTimeout(() => void tick(), EVERY_MS);
    };
    void tick();
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [register]);

  const latest = view && 'lines' in view ? view.lines.find((l) => l.latest)?.id : undefined;
  useEffect(() => {
    if (latest) list.current?.querySelector(`[data-line="${latest}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [latest]);

  const fullScreen = () => void document.documentElement.requestFullscreen?.().catch(() => undefined);

  if (error) {
    return (
      <main className="cd cd-center">
        <Icon name="alert" size={40} />
        <p>{error}</p>
      </main>
    );
  }
  if (!view) return <main className="cd cd-center" aria-busy="true" />;

  return (
    <main className={`cd cd-is-${view.phase}`} aria-live="polite">
      <header className="cd-top">
        <span className="cd-brand">
          <span className="cd-mark" aria-hidden="true">
            {view.business.trim().charAt(0).toUpperCase()}
          </span>
          {view.business}
        </span>
        <span className="cd-tools">
          <span className="cd-clock tabular">{clock(new Date(now))}</span>
          {can('pos.manage') ? <IconButton icon="edit" label={t('cd.edit_message')} onClick={() => setEditing(true)} /> : null}
          {document.fullscreenEnabled && !document.fullscreenElement ? <IconButton icon="layout" label={t('cd.full_screen')} onClick={fullScreen} /> : null}
        </span>
      </header>

      {view.phase === 'idle' ? (
        <section className="cd-welcome">
          <span className="cd-welcome-mark" aria-hidden="true">
            {view.business.trim().charAt(0).toUpperCase()}
          </span>
          <h1>{t('cd.welcome', { name: view.business })}</h1>
          {view.message ? <p className="cd-message">{view.message}</p> : null}
        </section>
      ) : view.phase === 'thanks' ? (
        <section className="cd-thanks">
          <span className="cd-thanks-icon">
            <Icon name="checkCircle" size={72} />
          </span>
          <h1>{t('cd.thanks')}</h1>
          <dl className="cd-thanks-sum">
            <div>
              <dt>{t('cd.paid')}</dt>
              <dd className="tabular">{money(view.received)}</dd>
            </div>
            {view.change > 0 ? (
              <div className="cd-change">
                <dt>{t('cd.change')}</dt>
                <dd className="tabular">{money(view.change)}</dd>
              </div>
            ) : null}
          </dl>
          <p className="cd-message">{t('cd.see_you')}</p>
        </section>
      ) : (
        <section className="cd-bill">
          <ul className="cd-lines" ref={list}>
            {view.lines.map((l) => (
              <li key={l.id} data-line={l.id} className={l.latest ? 'latest' : undefined}>
                <span className="cd-qty tabular">{l.qty}×</span>
                <span className="cd-name">
                  {l.name}
                  {l.combo ? <small>{l.combo}</small> : null}
                  {l.qty !== 1 ? <small className="tabular">{t('cd.each', { price: money(l.unit_price) })}</small> : null}
                </span>
                <span className="cd-amount tabular">
                  {l.discount ? <s>{money(l.total + l.discount)}</s> : null}
                  {money(l.total)}
                </span>
              </li>
            ))}
            {!view.lines.length ? <li className="cd-empty">{t('cd.starting')}</li> : null}
          </ul>
          <aside className="cd-total">
            {view.phase === 'paying' ? (
              <>
                <span className="cd-total-label">{t('cd.to_pay')}</span>
                <strong className="cd-total-amount tabular">{money(view.due)}</strong>
                {view.paid > 0 ? <span className="cd-total-note tabular">{t('cd.paid_so_far', { amount: money(view.paid) })}</span> : null}
              </>
            ) : (
              <>
                <span className="cd-total-label">{t('cd.total')}</span>
                <strong className="cd-total-amount tabular">{money(view.total)}</strong>
                <span className="cd-total-note">{tn('cd.items', view.count)}</span>
              </>
            )}
            <dl className="cd-total-rows">
              {view.discount > 0 ? (
                <div className="cd-saved">
                  <dt>{t('cd.you_save')}</dt>
                  <dd className="tabular">{money(view.discount)}</dd>
                </div>
              ) : null}
              <div>
                <dt>{view.tax_added ? t('cd.tax_added') : t('cd.vat_included')}</dt>
                <dd className="tabular">{money(view.tax)}</dd>
              </div>
            </dl>
          </aside>
        </section>
      )}
      {editing && view ? <MessageDialog register={register} current={view.message} onClose={() => setEditing(false)} /> : null}
    </main>
  );
}

function MessageDialog({ register, current, onClose }: { register: string; current: string | null; onClose: () => void }) {
  const { fail, toast } = useUi();
  const [text, setText] = useState(current ?? '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await action('pos.display_message', { register, message: text.trim() || null });
      toast(t('cd.message_saved'));
      onClose();
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('cd.edit_message')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" busy={busy} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Field label={t('cd.message')} hint={t('cd.message_hint')}>
        <input className="input" value={text} maxLength={140} onChange={(e) => setText(e.target.value)} data-autofocus />
      </Field>
    </Dialog>
  );
}
